import { memo, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FairUseTopUpTarget } from "./fair-use-top-up-dialog"
import type { FairUseOverviewFilter, FairUseOverviewRow } from "@/api/admin/fair-use"

import { downloadBillingExport } from "@/api/admin/billing"
import { AlertCircle, AlertTriangle, Info } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { useConfigQuery } from "@/hooks/use-config-query"

import { FairUseCoverageCard } from "./fair-use-coverage-card"
import { FairUseDenyDialog } from "./fair-use-deny-dialog"
import { FairUseFilters } from "./fair-use-filters"
import { FairUseKpis } from "./fair-use-kpis"
import { FairUseMonthBar } from "./fair-use-month-bar"
import { FairUseOpsEmpty } from "./fair-use-ops-empty"
import {
  FairUseOpsCardShell,
  FairUseOpsError,
  FairUseOpsLoading,
  FairUseOpsUnavailable,
} from "./fair-use-ops-placeholders"
import {
  currentUtcYearMonth,
  fairUseOpsShownTotal,
  resolveFairUseBlockCapSek,
  searchFairUseRows,
} from "./fair-use-ops-utils"
import { FairUseTopUpDialog } from "./fair-use-top-up-dialog"
import { FairUseUsageTable } from "./fair-use-usage-table"
import {
  fairUseDisplayName,
  fairUseErrorDescription,
  getRecentMonthOptions,
  isFairUseNotConfiguredError,
  sortFairUseRowsByCost,
} from "./fair-use-utils"
import {
  FAIR_USE_SUMMARY_QUERY_KEY,
  useApproveFairUseRequestMutation,
  useCreateFairUseGrantMutation,
  useDenyFairUseRequestMutation,
  useFairUseConfigQuery,
  useFairUseOverviewInfiniteQuery,
  useFairUseSummaryQuery,
  useLockBillingMonthMutation,
  useRevokeFairUseGrantMutation,
} from "../../hooks/queries/fair-use"

/**
 * The AI-budget operations card.
 *
 * One month at a time: who is spending it, who is waiting for an answer, and
 * the two month-level acts that close the books (export the billing basis,
 * lock the month). Everything that *configures* fair use — thresholds, plan
 * prices, delegation — lives in Configuration, not here: this card is the
 * daily surface, and mixing settings into it is what made the old one a scroll.
 */
export const FairUseOpsCard = memo(function FairUseOpsCard() {
  const { t } = useTranslation()

  // Same gate as the backend routes: platform admins always, customer admins
  // only with `manage` — so we never render a card whose every query 403s.
  const { data: appConfig } = useConfigQuery()
  const isPlatformAdmin = appConfig?.user_policy.is_platform_admin ?? false
  const canView =
    isPlatformAdmin || appConfig?.user_policy.admin_capabilities["fair_use"] === "manage"

  const monthOptions = useMemo(() => getRecentMonthOptions(6), [])
  const [month, setMonth] = useState<string>(monthOptions[0])
  const [filter, setFilter] = useState<FairUseOverviewFilter>("all")
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState("cost-desc")
  const [denyTarget, setDenyTarget] = useState<FairUseOverviewRow | null>(null)
  const [topUpOpen, setTopUpOpen] = useState(false)
  const [topUpTarget, setTopUpTarget] = useState<FairUseTopUpTarget | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  const summaryQuery = useFairUseSummaryQuery(month, canView)
  const overviewQuery = useFairUseOverviewInfiniteQuery(month, canView, filter)

  // The top-up size is quoted on a button a delegated admin presses, but the
  // config singleton it lives in is commercial and may be unreadable to them —
  // so the config query is a LAST resort, only fetched when neither the
  // summary nor the first page inlined the amount.
  const summary = summaryQuery.data
  const firstPage = overviewQuery.data?.pages[0]
  const needsConfigForPrice =
    canView && summary?.block_cap_sek == null && firstPage?.block_cap_sek == null
  const { data: fairUseConfig } = useFairUseConfigQuery(needsConfigForPrice)
  const blockCapSek = resolveFairUseBlockCapSek([
    summary?.block_cap_sek,
    firstPage?.block_cap_sek,
    fairUseConfig?.config.block_cap_sek,
  ])

  const approveMutation = useApproveFairUseRequestMutation()
  const denyMutation = useDenyFairUseRequestMutation()
  const grantMutation = useCreateFairUseGrantMutation()
  const revokeMutation = useRevokeFairUseGrantMutation()
  const lockMutation = useLockBillingMonthMutation()
  const queryClient = useQueryClient()
  const isCurrentMonth = month === currentUtcYearMonth()

  // Each page is cost-sorted server-side, but page 1 also carries the
  // zero-cost users (a top-up or a waiting request with no spend yet) appended
  // AFTER its sorted rows — so the concatenation has to be re-sorted, or the
  // table shows 0 SEK rows above page 2's heavier ones.
  const loadedRows = useMemo(
    () => sortFairUseRowsByCost(overviewQuery.data?.pages.flatMap((page) => page.users) ?? []),
    [overviewQuery.data]
  )
  const rows = useMemo(() => {
    const matched = [...searchFairUseRows(loadedRows, search)]
    if (sort === "cost-asc") return matched.sort((a, b) => a.cost_sek - b.cost_sek)
    if (sort.startsWith("name-"))
      return matched.sort((a, b) => {
        const names = fairUseDisplayName(a).localeCompare(fairUseDisplayName(b), undefined, {
          sensitivity: "base",
          numeric: true,
        })
        return sort === "name-desc" ? -names : names
      })
    return matched
  }, [loadedRows, search, sort])

  if (!canView) return null

  const failed = (title: string) => (error: unknown) =>
    toast.error(title, { description: fairUseErrorDescription(error, t) })

  const handleApprove = (row: FairUseOverviewRow) => {
    if (!row.pending_request) return
    approveMutation.mutate(
      { requestId: row.pending_request.id, blocks: 1, month },
      {
        onSuccess: () =>
          toast.success(t("adminDashboard.fairUse.toastApproved", "Request approved")),
        onError: failed(t("adminDashboard.fairUse.toastApproveError", "Failed to approve request")),
      }
    )
  }

  const handleDeny = (reason: string) => {
    if (!denyTarget?.pending_request) return
    denyMutation.mutate(
      { requestId: denyTarget.pending_request.id, reason, month },
      {
        onSuccess: () => {
          toast.success(t("adminDashboard.fairUse.toastDenied", "Request denied"))
          setDenyTarget(null)
        },
        onError: failed(t("adminDashboard.fairUse.toastDenyError", "Failed to deny request")),
      }
    )
  }

  const handleTopUp = (args: { userId: string; blocks: number; reason?: string }) => {
    grantMutation.mutate(
      { user_id: args.userId, blocks: args.blocks, reason: args.reason, month },
      {
        onSuccess: () => {
          toast.success(t("adminDashboard.fairUse.ops.toastToppedUp", "Budget topped up"))
          setTopUpOpen(false)
        },
        onError: failed(t("adminDashboard.fairUse.ops.toastTopUpError", "Could not top up")),
      }
    )
  }

  const handleRevoke = (_row: FairUseOverviewRow, grantId: string) => {
    revokeMutation.mutate(
      { grantId, month },
      {
        onSuccess: () =>
          toast.success(t("adminDashboard.fairUse.ops.toastRevoked", "Top-up withdrawn")),
        onError: failed(
          t("adminDashboard.fairUse.ops.toastRevokeError", "Could not withdraw the top-up")
        ),
      }
    )
  }

  const handleLock = () => {
    lockMutation.mutate(
      { month },
      {
        onSuccess: () => toast.success(t("adminDashboard.fairUse.ops.toastLocked", "Month locked")),
        onError: failed(t("adminDashboard.fairUse.ops.toastLockError", "Could not lock the month")),
      }
    )
  }

  const handleExport = () => {
    // GET .../export.csv lazy-locks a still-open PAST month. After that freeze
    // the summary must refetch or the pill stays "Month open".
    const freezesMonth = Boolean(summary?.lock && !summary.lock.locked && !isCurrentMonth)
    setIsExporting(true)
    void downloadBillingExport(month)
      .then(() => {
        if (freezesMonth) {
          void queryClient.invalidateQueries({ queryKey: FAIR_USE_SUMMARY_QUERY_KEY })
        }
      })
      .catch(failed(t("adminDashboard.fairUse.ops.toastExportError", "Could not export the month")))
      .finally(() => setIsExporting(false))
  }

  const openTopUpFor = (row: FairUseOverviewRow) => {
    setTopUpTarget({ userId: row.user, label: fairUseDisplayName(row) })
    setTopUpOpen(true)
  }

  if (overviewQuery.isLoading || summaryQuery.isLoading) return <FairUseOpsLoading />

  // No first page: either fair use genuinely isn't provisioned (503
  // `fair_use_not_configured`) or the month failed for a reason the admin
  // needs to see. Only the former may claim the feature is off.
  if (!firstPage) {
    const error = overviewQuery.error ?? summaryQuery.error
    if (!overviewQuery.isError || isFairUseNotConfiguredError(error))
      return <FairUseOpsUnavailable />
    return (
      <FairUseOpsError
        error={error}
        isFetching={overviewQuery.isFetching}
        onRetry={() => void overviewQuery.refetch()}
      />
    )
  }

  // Filtered views are unpaged: `matched` is the complete match count and
  // `users` is only `matched[:limit]`. The month's `totals.users` is the
  // unfiltered population and must not be used as "of M" while a filter is
  // on — that presents a truncated match set as the whole month.
  const total = fairUseOpsShownTotal({
    filter,
    matched: firstPage.matched,
    totalsUsers: firstPage.totals?.users,
    summaryUsers: summary?.users,
    loadedCount: loadedRows.length,
  })
  // Never claim fewer users exist than are on screen: if the loaded rows
  // outrun the known total (a stale or under-counting total slipping through
  // for a moment), show the count alone rather than a nonsensical "N of M".
  const showTotal = total >= rows.length ? total : undefined
  const remainingMatched =
    filter !== "all" && typeof firstPage.matched === "number"
      ? Math.max(0, firstPage.matched - loadedRows.length)
      : 0
  const listTruncated = firstPage.truncated === true && remainingMatched > 0
  const monthTruncated = summary?.truncated === true

  return (
    <>
      <FairUseOpsCardShell
        actions={
          <FairUseMonthBar
            isCurrentMonth={isCurrentMonth}
            isExporting={isExporting}
            isLocking={lockMutation.isPending}
            lock={summary?.lock ?? null}
            month={month}
            monthOptions={monthOptions}
            onExport={handleExport}
            onLock={handleLock}
            onMonthChange={(next) => {
              setMonth(next)
              setSearch("")
            }}
            onTopUp={() => {
              setTopUpTarget(null)
              setTopUpOpen(true)
            }}
          />
        }
      >
        {!firstPage.enabled && (
          <div className="bg-muted/50 text-muted-foreground flex items-center gap-2 rounded-xl border p-3 text-sm">
            <Info className="size-4 shrink-0" />
            {t(
              "adminDashboard.fairUse.ops.disabledBanner",
              "Budget limits are turned off — usage is tracked, but nobody is warned or stopped."
            )}
          </div>
        )}

        {summary && (
          <>
            <FairUseKpis
              filter={filter}
              summary={summary}
              warnPercent={fairUseConfig?.config.warn_percent}
              onFilterChange={setFilter}
            />
            <FairUseFilters
              filter={filter}
              partial={overviewQuery.hasNextPage}
              search={search}
              sort={sort}
              summary={summary}
              onFilterChange={setFilter}
              onSearchChange={setSearch}
              onSortChange={setSort}
            />
          </>
        )}

        {(listTruncated || monthTruncated) && (
          <div
            className="border-warning/50 bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-xl border p-3 text-sm"
            data-testid="fair-use-truncated-warning"
            role="status"
          >
            <AlertTriangle className="size-4 shrink-0" />
            <span>
              {listTruncated
                ? t(
                    "adminDashboard.fairUse.ops.truncatedFilter",
                    "Showing {{shown}} of {{matched}} matching users. {{remaining}} more matched and are not listed — this filter is not paged.",
                    {
                      shown: loadedRows.length,
                      matched: firstPage.matched,
                      remaining: remainingMatched,
                    }
                  )
                : t(
                    "adminDashboard.fairUse.ops.truncatedMonth",
                    "Some counts are a lower bound — not every user in the month was scanned."
                  )}
            </span>
          </div>
        )}

        {rows.length === 0 ? (
          <FairUseOpsEmpty
            filter={filter}
            search={search}
            onClearSearch={() => setSearch("")}
            onShowAll={() => setFilter("all")}
            onTopUp={() => {
              setTopUpTarget(null)
              setTopUpOpen(true)
            }}
          />
        ) : (
          <FairUseUsageTable
            blockCapSek={blockCapSek}
            enforced={firstPage.enabled}
            isPlatformAdmin={isPlatformAdmin}
            isResolvingRequest={approveMutation.isPending || denyMutation.isPending}
            rows={rows}
            onApprove={handleApprove}
            onDeny={setDenyTarget}
            onRevokeGrant={handleRevoke}
            onTopUpUser={openTopUpFor}
          />
        )}

        {/* A failed continuation keeps page 1 on screen, so without this the
            "Show more" click just looks like a no-op (a stale or malformed
            continuation token answers 400). */}
        {(rows.length > 0 || overviewQuery.isFetchNextPageError) && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
            <span className="text-muted-foreground text-xs">
              {showTotal !== undefined
                ? t("adminDashboard.fairUse.ops.showingCount", "Showing {{shown}} of {{total}}", {
                    shown: rows.length,
                    total: showTotal,
                  })
                : t("adminDashboard.fairUse.ops.showingCountUnknownTotal", "Showing {{shown}}", {
                    shown: rows.length,
                  })}
            </span>
            {overviewQuery.hasNextPage && (
              <Button
                disabled={overviewQuery.isFetchingNextPage}
                size="sm"
                type="button"
                variant="outline"
                onClick={() => void overviewQuery.fetchNextPage()}
              >
                {overviewQuery.isFetchingNextPage
                  ? t("adminDashboard.fairUse.loadingMore", "Loading…")
                  : t("adminDashboard.fairUse.showMore", "Show more")}
              </Button>
            )}
            {overviewQuery.isFetchNextPageError && (
              <div className="flex flex-wrap items-center gap-2 text-sm" role="alert">
                <AlertCircle className="text-destructive size-4 shrink-0" />
                <span className="text-destructive">
                  {t("adminDashboard.fairUse.loadMoreError", "Could not load more users")}
                </span>
                <span className="text-muted-foreground">
                  {fairUseErrorDescription(overviewQuery.error, t)}
                </span>
                <Button
                  disabled={overviewQuery.isFetchingNextPage}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={() => void overviewQuery.fetchNextPage()}
                >
                  {t("adminDashboard.fairUse.retry", "Try again")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Is what we meter what Azure billed? The people table above cannot
            answer that — this is the only surface that compares the two. */}
        <FairUseCoverageCard isPlatformAdmin={isPlatformAdmin} />
      </FairUseOpsCardShell>

      <FairUseDenyDialog
        isPending={denyMutation.isPending}
        motivation={denyTarget?.pending_request?.motivation}
        open={denyTarget !== null}
        userLabel={denyTarget ? fairUseDisplayName(denyTarget) : ""}
        onSubmit={handleDeny}
        onOpenChange={(open) => {
          if (!open) setDenyTarget(null)
        }}
      />

      <FairUseTopUpDialog
        blockCapSek={blockCapSek}
        blockListPriceSek={fairUseConfig?.config.block_list_price_sek}
        isPending={grantMutation.isPending}
        open={topUpOpen}
        target={topUpTarget}
        onOpenChange={setTopUpOpen}
        onSubmit={handleTopUp}
      />
    </>
  )
})
